import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';

interface UseCasesSectionProps {
    activeJourneyId: string | null;
}

export const UseCasesSection: React.FC<UseCasesSectionProps> = ({ activeJourneyId }) => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    
    // Load saved index from localStorage, default to 0 if not found
    const [currentIndex, setCurrentIndex] = useState(() => {
        const saved = localStorage.getItem('useCaseCarouselIndex');
        return saved !== null ? parseInt(saved, 10) : 0;
    });

    // Save index to localStorage whenever it changes
    React.useEffect(() => {
        localStorage.setItem('useCaseCarouselIndex', currentIndex.toString());
    }, [currentIndex]);

    const handleStartupJourneyClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (activeJourneyId) {
            navigate(`/journey/${activeJourneyId}`);
        } else {
            navigate('/startup-journey/setup');
        }
    };

    const useCases = [
        // Use Case 1: AI Startup Journey (Special case with onClick)
        {
            id: 'case1',
            icon: t('useCases.case1.icon'),
            title: t('useCases.case1.title'),
            scenario: t('useCases.case1.scenario'),
            steps: [
                t('useCases.case1.step1'),
                t('useCases.case1.step2'),
                t('useCases.case1.step3'),
                t('useCases.case1.step4'),
                t('useCases.case1.step5'),
            ],
            outcome: t('useCases.case1.outcome'),
            onClick: handleStartupJourneyClick,
            ctaIcon: 'bi-rocket-takeoff',
            ctaText: t('useCases.case1.cta'),
        },        
        // Use Case 2: Get users to sign in
        {
            id: 'case2',
            icon: t('useCases.case2.icon'),
            title: t('useCases.case2.title'),
            scenario: t('useCases.case2.scenario'),
            steps: [
                t('useCases.case2.step1'),
                t('useCases.case2.step2'),
                t('useCases.case2.step3'),
                t('useCases.case2.step4'),
                t('useCases.case2.step5'),
            ],
            outcome: t('useCases.case2.outcome'),
            ctaLink: '/create-task',
            ctaIcon: 'bi-plus-circle',
            ctaText: t('useCases.case2.cta'),
        },

        // Use Case 3: Survey - validate idea
        {
            id: 'case3',
            icon: t('useCases.case3.icon'),
            title: t('useCases.case3.title'),
            scenario: t('useCases.case3.scenario'),
            steps: [
                t('useCases.case3.step1'),
                t('useCases.case3.step2'),
                t('useCases.case3.step3'),
                t('useCases.case3.step4'),
                t('useCases.case3.step5'),
            ],
            outcome: t('useCases.case3.outcome'),
            ctaLink: '/create-questionnaire',
            ctaIcon: 'bi-plus-circle',
            ctaText: t('useCases.case3.cta'),
        },
        // Use Case 4: Market research survey
        {
            id: 'case4',
            icon: t('useCases.case4.icon'),
            title: t('useCases.case4.title'),
            scenario: t('useCases.case4.scenario'),
            steps: [
                t('useCases.case4.step1'),
                t('useCases.case4.step2'),
                t('useCases.case4.step3'),
                t('useCases.case4.step4'),
                t('useCases.case4.step5'),
            ],
            outcome: t('useCases.case4.outcome'),
            ctaLink: '/create-questionnaire',
            ctaIcon: 'bi-plus-circle',
            ctaText: t('useCases.case4.cta'),
        },
        // Use Case 5: Pre-release project
        {
            id: 'case5',
            icon: t('useCases.case5.icon'),
            title: t('useCases.case5.title'),
            scenario: t('useCases.case5.scenario'),
            steps: [
                t('useCases.case5.step1'),
                t('useCases.case5.step2'),
                t('useCases.case5.step3'),
                t('useCases.case5.step4'),
                t('useCases.case5.step5'),
            ],
            outcome: t('useCases.case5.outcome'),
            ctaLink: '/create-pre-release-project',
            ctaIcon: 'bi-rocket-takeoff',
            ctaText: t('useCases.case5.cta'),
        },
        // Use Case 6: Build team
        {
            id: 'case6',
            icon: t('useCases.case6.icon'),
            title: t('useCases.case6.title'),
            scenario: t('useCases.case6.scenario'),
            steps: [
                t('useCases.case6.step1'),
                t('useCases.case6.step2'),
                t('useCases.case6.step3'),
                t('useCases.case6.step4'),
                t('useCases.case6.step5'),
            ],
            outcome: t('useCases.case6.outcome'),
            ctaLink: '/create-team-request',
            ctaIcon: 'bi-people-fill',
            ctaText: t('useCases.case6.cta'),
        },
        // Use Case 7: Website testing
        {
            id: 'case7',
            icon: t('useCases.case7.icon'),
            title: t('useCases.case7.title'),
            scenario: t('useCases.case7.scenario'),
            steps: [
                t('useCases.case7.step1'),
                t('useCases.case7.step2'),
                t('useCases.case7.step3'),
                t('useCases.case7.step4'),
                t('useCases.case7.step5'),
            ],
            outcome: t('useCases.case7.outcome'),
            ctaLink: '/create-task',
            ctaIcon: 'bi-plus-circle',
            ctaText: t('useCases.case7.cta'),
        },
        // Use Case 8: Forum advice
        {
            id: 'case8',
            icon: t('useCases.case8.icon'),
            title: t('useCases.case8.title'),
            scenario: t('useCases.case8.scenario'),
            steps: [
                t('useCases.case8.step1'),
                t('useCases.case8.step2'),
                t('useCases.case8.step3'),
                t('useCases.case8.step4'),
                t('useCases.case8.step5'),
            ],
            outcome: t('useCases.case8.outcome'),
            ctaLink: '/forums',
            ctaIcon: 'bi-chat-dots',
            ctaText: t('useCases.case8.cta'),
        },
        // Use Case 9: Landing Page Testing
        {
            id: 'case9',
            icon: t('useCases.case9.icon'),
            title: t('useCases.case9.title'),
            scenario: t('useCases.case9.scenario'),
            steps: [
                t('useCases.case9.step1'),
                t('useCases.case9.step2'),
                t('useCases.case9.step3'),
                t('useCases.case9.step4'),
                t('useCases.case9.step5'),
            ],
            outcome: t('useCases.case9.outcome'),
            ctaLink: '/create-task',
            ctaIcon: 'bi-clipboard-check',
            ctaText: t('useCases.case9.cta'),
        },
        // Use Case 10: Pricing Validation
        {
            id: 'case10',
            icon: t('useCases.case10.icon'),
            title: t('useCases.case10.title'),
            scenario: t('useCases.case10.scenario'),
            steps: [
                t('useCases.case10.step1'),
                t('useCases.case10.step2'),
                t('useCases.case10.step3'),
                t('useCases.case10.step4'),
                t('useCases.case10.step5'),
            ],
            outcome: t('useCases.case10.outcome'),
            ctaLink: '/create-pre-release-project',
            ctaIcon: 'bi-cash-coin',
            ctaText: t('useCases.case10.cta'),
        },
        // Use Case 11: Pitch Deck Feedback
        {
            id: 'case11',
            icon: t('useCases.case11.icon'),
            title: t('useCases.case11.title'),
            scenario: t('useCases.case11.scenario'),
            steps: [
                t('useCases.case11.step1'),
                t('useCases.case11.step2'),
                t('useCases.case11.step3'),
                t('useCases.case11.step4'),
                t('useCases.case11.step5'),
            ],
            outcome: t('useCases.case11.outcome'),
            ctaLink: '/create-task',
            ctaIcon: 'bi-easel',
            ctaText: t('useCases.case11.cta'),
        },
        // Use Case 12: Beta Tester Recruitment
        {
            id: 'case12',
            icon: t('useCases.case12.icon'),
            title: t('useCases.case12.title'),
            scenario: t('useCases.case12.scenario'),
            steps: [
                t('useCases.case12.step1'),
                t('useCases.case12.step2'),
                t('useCases.case12.step3'),
                t('useCases.case12.step4'),
                t('useCases.case12.step5'),
            ],
            outcome: t('useCases.case12.outcome'),
            ctaLink: '/create-pre-release-project',
            ctaIcon: 'bi-people-fill',
            ctaText: t('useCases.case12.cta'),
        },
    ];

    const handlePrevious = () => {
        setCurrentIndex((prev) => {
            // Move back by 2, but ensure we don't go below 0
            const newIndex = prev - 2;
            return newIndex < 0 ? Math.max(0, useCases.length - 2) : newIndex;
        });
    };

    const handleNext = () => {
        setCurrentIndex((prev) => {
            // Move forward by 2, but wrap around if needed
            const newIndex = prev + 2;
            return newIndex >= useCases.length ? 0 : newIndex;
        });
    };

    const currentUseCase = useCases[currentIndex];

    return (
        <section className="use-cases-section">
            <div className="quick-nav-container">
                <h2 className="quick-nav-title">{t('useCases.title')}</h2>
                <p className="quick-nav-subtitle">{t('useCases.subtitle')}</p>

                {/* Carousel Navigation */}
                <div className="use-case-carousel-nav" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '2rem', marginBottom: '2rem' }}>
                    <button
                        onClick={handlePrevious}
                        className="use-case-nav-button"
                        aria-label="Previous use cases"
                    >
                        <i className="bi bi-chevron-left"></i>
                    </button>
                    <div className="use-case-counter" style={{ fontSize: '1.1rem', fontWeight: 600, minWidth: '120px', textAlign: 'center' }}>
                        {currentIndex + 1}-{Math.min(currentIndex + 2, useCases.length)} / {useCases.length}
                    </div>
                    <button
                        onClick={handleNext}
                        className="use-case-nav-button"
                        aria-label="Next use cases"
                    >
                        <i className="bi bi-chevron-right"></i>
                    </button>
                </div>

                {/* Two Use Cases Display */}
                <div className="use-cases-carousel" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
                    {/* First Use Case */}
                    <div key={currentUseCase.id} className="use-case-card">
                        <div className="use-case-header">
                            <span className="use-case-icon">{currentUseCase.icon}</span>
                            <h3>{currentUseCase.title}</h3>
                        </div>
                        <div className="use-case-body">
                            <p className="use-case-scenario">
                                <strong>{t('useCases.scenarioLabel')}</strong> {currentUseCase.scenario}
                            </p>
                            <div className="use-case-steps">
                                {currentUseCase.steps.map((step: string, index: number) => (
                                    <div key={index} className="step-item">
                                        <span className="step-num">{index + 1}</span> {step}
                                    </div>
                                ))}
                            </div>
                            <p className="use-case-outcome">
                                ✅ <strong>{t('useCases.outcomeLabel')}</strong> {currentUseCase.outcome}
                            </p>
                        </div>
                        {currentUseCase.onClick ? (
                            <a href="#" onClick={currentUseCase.onClick} className="use-case-cta">
                                <i className={`bi ${currentUseCase.ctaIcon}`}></i> {currentUseCase.ctaText}
                            </a>
                        ) : (
                            <Link to={currentUseCase.ctaLink!} className="use-case-cta">
                                <i className={`bi ${currentUseCase.ctaIcon}`}></i> {currentUseCase.ctaText}
                            </Link>
                        )}
                    </div>

                    {/* Second Use Case */}
                    {currentIndex + 1 < useCases.length && (() => {
                        const secondUseCase = useCases[currentIndex + 1];
                        return (
                            <div key={secondUseCase.id} className="use-case-card">
                                <div className="use-case-header">
                                    <span className="use-case-icon">{secondUseCase.icon}</span>
                                    <h3>{secondUseCase.title}</h3>
                                </div>
                                <div className="use-case-body">
                                    <p className="use-case-scenario">
                                        <strong>{t('useCases.scenarioLabel')}</strong> {secondUseCase.scenario}
                                    </p>
                                    <div className="use-case-steps">
                                        {secondUseCase.steps.map((step: string, index: number) => (
                                            <div key={index} className="step-item">
                                                <span className="step-num">{index + 1}</span> {step}
                                            </div>
                                        ))}
                                    </div>
                                    <p className="use-case-outcome">
                                        ✅ <strong>{t('useCases.outcomeLabel')}</strong> {secondUseCase.outcome}
                                    </p>
                                </div>
                                {secondUseCase.onClick ? (
                                    <a href="#" onClick={secondUseCase.onClick} className="use-case-cta">
                                        <i className={`bi ${secondUseCase.ctaIcon}`}></i> {secondUseCase.ctaText}
                                    </a>
                                ) : (
                                    <Link to={secondUseCase.ctaLink!} className="use-case-cta">
                                        <i className={`bi ${secondUseCase.ctaIcon}`}></i> {secondUseCase.ctaText}
                                    </Link>
                                )}
                            </div>
                        );
                    })()}
                </div>
            </div>
        </section>
    );
};
